export interface Speaker {
  name: string;
  photo?: string;
  urlBio?: string;
  puesto?: string;
}

export interface Talk {
  title?: string;
  description?: string;
  speakers?: Speaker[];
  room: string;
  disabled?: boolean;
}

export interface ScheduleSlot {
  time: string;
  type: "talk" | "break" | "activity" | "post-event";
  title?: string;
  description?: string;
  location?: string;
  mapLink?: string;
  speakers?: Speaker[];
  talks?: Talk[];
}

const ROOMS = ["Factory", "Naranja", "Open Innovation", "Auditorium"] as const;

const openTalks = (): Talk[] => ROOMS.map((room) => ({ room }));

export const defaultScheduleData: ScheduleSlot[] = [
  {
    time: "09:00 - 09:15",
    title: "Bienvenida y acreditaciones",
    location: "Entrada principal",
    type: "activity",
  },
  {
    time: "09:15 - 09:45",
    title: "Apertura",
    description: "Dinámica de apertura del evento",
    location: "SAL-ON",
    type: "activity",
  },
  {
    time: "09:45 - 10:30",
    type: "talk",
    talks: [
      { title: "De los blogs a TikTok: ¿dónde se fue tu atención?", room: "FACTORY", speakers: [
        {
          name: "Gabriela Bronte"
        },
      ] },
      { title: "PAN-PLINAS", room: "SALA NARANJA" , speakers: [
        {
          name: "Urtzi Arostegui"
        },
      ] },
      { title: "Ganar certidumbre con modelos de lenguaje", room: "SALA AUDITORIUM", speakers: [
        {
          name: "Karlos G. Liberal"
        },
      ] },

      { title: "Sobrevivir profesionalmente en tiempos de IA", room: "OPEN INNOVATION", speakers: [
        {
          name: "Diego Rodriguez"
        },
      ] },
    ],
  },
  {
    time: "10:30 - 11:15",
    title: "Descanso",
    type: "break",
  },
  {
    time: "11:15 - 12:00",
    type: "talk",
    talks: [
      { title: "¿Chati, salvamos a 5 sacrificando a 1?", room: "FACTORY", speakers: [
        {
          name: "Ujué Agudo"
        },
      ] },
      { title: "Dejar de entregar pantallas y entregar el sistema", room: "SALA NARANJA" , speakers: [
        {
          name: "Olaia Merino"
        },
      ] },
      { title: "Construyendo el sistema que construye el sistema", room: "SALA AUDITORIUM", speakers: [
        {
          name: "Ismael Liberal"
        },
      ] },
      { title: "Japonés explicado por una UX/UI y un developer", room: "OPEN INNOVATION", speakers: [
        {
          name: "Eva de Haro y John Bayona"
        },
      ] },
    ],
  },
  {
    time: "12:00 - 12:30",
    title: "Foto de grupo y almuerzo",
    location: "SAL-ON",
    type: "break",
  },
  {
    time: "12:30 - 13:30",
    title: "De los presupuestos al código fuente: quince años peleando por abrir lo público",
    location: "SAL-ON",
    speakers: [
      {
        name: "David Cabo",
        photo: "https://civio.es/uploads/team/firma-david-2022_250px.jpg",
        urlBio: "https://civio.es/equipo/david-cabo/",
        puesto: "Codirector de Civio",
      },
    ],
    type: "activity",
  },
  {
    time: "13:30 - 14:00",
    title: "Ritual de pizarra: preparación Open",
    location: "SAL-ON",
    type: "activity",
  },
  {
    time: "14:30 - 15:00",
    type: "talk",
    talks: openTalks(),
  },
  {
    time: "15:00 - 15:45",
    title: "Comida",
    location: "SAL-ON",
    type: "break",
  },
  {
    time: "16:00 - 16:30",
    type: "talk",
    talks: openTalks(),
  },
  {
    time: "16:30 - 17:30",
    title: "Networking, retrospectiva y cierre",
    description: "Clausura del evento y últimas palabras",
    location: "SAL-ON",
    type: "activity",
  },
  {
    time: "18:00",
    title: "Post-open",
    description:
      "El networking no termina aquí: acompáñanos para seguir conectando y continuar esas charlas que se han quedado cortas",
    location: "The Beer Garden",
    mapLink: "https://maps.app.goo.gl/fruA72swtAD7UgFE6",
    type: "post-event",
  },
];

const SHEET_ID = import.meta.env.VITE_SHEET_ID || "";
const SHEET_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json&sheet=Programacion&headers=1`;
const OPEN_TIMES = new Set(["14:30 - 15:00", "16:00 - 16:30"]);

const cell = (row: { c?: ({ f?: string; v?: string } | null)[] }, i: number) =>
  row.c?.[i]?.f || row.c?.[i]?.v || "";

function talksByOpenTime(rows: { c?: ({ f?: string; v?: string } | null)[] }[]) {
  const byTime = new Map<string, Talk[]>();
  for (const row of rows) {
    const time = cell(row, 0);
    if (!OPEN_TIMES.has(time)) continue;
    try {
      byTime.set(time, JSON.parse(cell(row, 6) || "[]"));
    } catch {
      console.warn(`talks_json inválido en ${time}`);
    }
  }
  return byTime;
}

export async function fetchScheduleData(): Promise<ScheduleSlot[]> {
  if (!SHEET_ID) return defaultScheduleData;

  try {
    const text = await (await fetch(SHEET_URL)).text();
    const json = JSON.parse(text.substring(47).slice(0, -2));
    const fromSheet = talksByOpenTime(json.table.rows);

    return defaultScheduleData.map((slot) => {
      const incoming = fromSheet.get(slot.time);
      if (!incoming?.length || !slot.talks) return slot;
      return {
        ...slot,
        talks: slot.talks.map((base, i) => {
          const talk = incoming[i];
          return talk ? { ...base, ...talk, room: talk.room || base.room } : base;
        }),
      };
    });
  } catch (error) {
    console.error("Error al cargar datos desde Google Sheets:", error);
    return defaultScheduleData;
  }
}
